import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { STORAGE_KEYS } from '#/shared/lib/storage'

import { DEFAULT_TABLE_DENSITY } from './density'
import type { TableDensity } from './density'

interface TableDensityState {
  /** Global table row density, persisted across sessions. */
  density: TableDensity
  setDensity: (density: TableDensity) => void
}

export const useTableDensityStore = create<TableDensityState>()(
  persist(
    (set) => ({
      density: DEFAULT_TABLE_DENSITY,
      setDensity: (density) => set({ density }),
    }),
    {
      name: STORAGE_KEYS.tableDensity.key,
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

/** Current global table density. */
export const useTableDensity = () => useTableDensityStore((s) => s.density)

/** Setter for the global table density. */
export const useSetTableDensity = () =>
  useTableDensityStore((s) => s.setDensity)

interface TableAppearanceState {
  /** Vertical dividers between columns/cells. Off by default. */
  cellDividers: boolean
  /** Horizontal dividers between rows. On by default. */
  rowDividers: boolean
  /** Zebra striping (alternating row background). Off by default. */
  stripedRows: boolean
  setCellDividers: (value: boolean) => void
  setRowDividers: (value: boolean) => void
  setStripedRows: (value: boolean) => void
}

export const useTableAppearanceStore = create<TableAppearanceState>()(
  persist(
    (set) => ({
      cellDividers: false,
      rowDividers: true,
      stripedRows: false,
      setCellDividers: (cellDividers) => set({ cellDividers }),
      setRowDividers: (rowDividers) => set({ rowDividers }),
      setStripedRows: (stripedRows) => set({ stripedRows }),
    }),
    {
      name: STORAGE_KEYS.tableAppearance.key,
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export const useTableCellDividers = () =>
  useTableAppearanceStore((s) => s.cellDividers)
export const useSetTableCellDividers = () =>
  useTableAppearanceStore((s) => s.setCellDividers)

export const useTableRowDividers = () =>
  useTableAppearanceStore((s) => s.rowDividers)
export const useSetTableRowDividers = () =>
  useTableAppearanceStore((s) => s.setRowDividers)

export const useTableStripedRows = () =>
  useTableAppearanceStore((s) => s.stripedRows)
export const useSetTableStripedRows = () =>
  useTableAppearanceStore((s) => s.setStripedRows)
